import { useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { Layers, Upload, Download, FileSpreadsheet } from "lucide-react";
import { useCatalog, priceFor } from "@/components/billing/LineItemsEditor";

const MATCH = {
  existing: ["bg-emerald-50 text-emerald-700 border-emerald-200", "Existing driver"],
  new: ["bg-sky-50 text-sky-700 border-sky-200", "New driver"],
  skip: ["bg-rose-50 text-rose-700 border-rose-200", "Skipped"],
};

export function BulkRequestDialog({ catalog, isAdmin, customers = [], onDone }) {
  const [open, setOpen] = useState(false);
  const [file, setFile] = useState(null);
  const [customerId, setCustomerId] = useState("");
  const [parsed, setParsed] = useState(null);
  const [included, setIncluded] = useState({});
  const [types, setTypes] = useState(["MVR"]);
  const [label, setLabel] = useState("");
  const [busy, setBusy] = useState(false);

  const reset = () => { setFile(null); setParsed(null); setIncluded({}); setTypes(["MVR"]); setLabel(""); setCustomerId(""); };
  const catalogTypes = Object.entries(catalog).filter(([code, v]) => code !== "RUSH" && !(v.per_unit && code.startsWith("BGC"))).map(([code, v]) => ({ code, ...v })).sort((a, b) => a.label.localeCompare(b.label));
  const rows = parsed?.rows || [];
  const chosen = rows.filter((r) => included[r.row] && r.match !== "skip");
  const { statePrices } = useCatalog();
  const priceRow = (r) => types.reduce((s, t) => s + priceFor(t, catalog, statePrices, (r.license_state || "").toUpperCase()), 0);
  const estimate = chosen.reduce((s, r) => s + priceRow(r), 0);
  const hasPrices = catalogTypes.some((c) => c.price != null);
  const hasStateFee = types.some((t) => catalog[t]?.state_fee);

  const parse = async () => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", file); if (customerId) fd.append("customer_id", customerId);
      const { data } = await api.post("/reports/bulk/parse", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setParsed(data); setIncluded(Object.fromEntries(data.rows.map((r) => [r.row, r.match !== "skip"])));
      toast.success(`${data.total} drivers read · ${data.existing} matched, ${data.new} new${data.skipped ? `, ${data.skipped} skipped` : ""}`);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  const submit = async () => {
    if (!chosen.length) { toast.error("No drivers selected"); return; }
    if (!types.length) { toast.error("Select at least one report type"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/reports/bulk/submit", {
        rows: chosen.map(({ first_name, last_name, license_number, license_state, dob, ssn, driver_id }) => ({ first_name, last_name, license_number, license_state, dob, ssn, driver_id })),
        report_types: types, customer_id: customerId || null, label: label || null,
      });
      toast.success(`${data.requests_created} requests created${data.drivers_created ? ` · ${data.drivers_created} new drivers added` : ""}`);
      setOpen(false); reset(); onDone?.();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  const downloadTemplate = async () => {
    const res = await api.get("/reports/bulk/template", { responseType: "blob" });
    const url = URL.createObjectURL(res.data); const a = document.createElement("a"); a.href = url; a.download = "driver-roster-template.csv"; a.click(); URL.revokeObjectURL(url);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) reset(); }}>
      <DialogTrigger asChild>
        <Button variant="outline" data-testid="bulk-request-button"><Layers className="h-4 w-4 mr-2" /> Bulk Annual Request</Button>
      </DialogTrigger>
      <DialogContent className="max-w-4xl max-h-[88vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Bulk Annual Request</DialogTitle></DialogHeader>
        <p className="text-sm text-slate-600">Upload your driver roster as a spreadsheet (CSV / Excel) or PDF. We match drivers already in the portal by license number or name, add any new ones, and create one request per driver.</p>

        <div className="grid md:grid-cols-[1fr_auto] gap-3 items-end rounded-md border border-slate-200 bg-slate-50 p-3">
          <div className="space-y-2">
            {isAdmin && (
              <div><Label className="text-xs">On behalf of customer</Label>
                <Select value={customerId} onValueChange={setCustomerId}><SelectTrigger data-testid="bulk-customer-select"><SelectValue placeholder="Select customer…" /></SelectTrigger>
                  <SelectContent>{customers.map((c) => <SelectItem key={c.id} value={c.id}>{c.company || c.name}</SelectItem>)}</SelectContent></Select></div>
            )}
            <div><Label className="text-xs">Roster file (.csv, .xlsx, .pdf)</Label>
              <Input type="file" accept=".csv,.xlsx,.xlsm,.pdf,.txt,text/csv,application/pdf,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" onChange={(e) => { setFile(e.target.files?.[0] || null); setParsed(null); }} data-testid="bulk-file-input" /></div>
          </div>
          <div className="flex flex-col gap-2">
            <Button onClick={parse} disabled={!file || busy || (isAdmin && !customerId)} className="bg-blue-700 hover:bg-blue-800" data-testid="bulk-parse-button"><Upload className="h-4 w-4 mr-2" />{busy && !parsed ? "Reading…" : "Read roster"}</Button>
            <button type="button" onClick={downloadTemplate} className="text-xs text-blue-700 hover:underline inline-flex items-center gap-1" data-testid="bulk-template-link"><Download className="h-3 w-3" />CSV template</button>
          </div>
        </div>

        {parsed && (
          <>
            <div className="flex flex-wrap items-center gap-2 text-sm" data-testid="bulk-summary">
              <FileSpreadsheet className="h-4 w-4 text-slate-500" /><span className="font-medium">{parsed.total} rows</span>
              <Badge className={`${MATCH.existing[0]} border`}>{parsed.existing} existing</Badge>
              <Badge className={`${MATCH.new[0]} border`}>{parsed.new} new</Badge>
              {parsed.skipped > 0 && <Badge className={`${MATCH.skip[0]} border`}>{parsed.skipped} skipped</Badge>}
            </div>
            <div className="rounded-md border border-slate-200 max-h-72 overflow-y-auto">
              <Table>
                <TableHeader><TableRow><TableHead className="w-8"><Checkbox checked={chosen.length > 0 && chosen.length === rows.filter((r) => r.match !== "skip").length} onCheckedChange={(v) => setIncluded(Object.fromEntries(rows.map((r) => [r.row, !!v && r.match !== "skip"])))} data-testid="bulk-select-all" /></TableHead><TableHead>Driver</TableHead><TableHead>License</TableHead><TableHead>DOB</TableHead><TableHead>Match</TableHead></TableRow></TableHeader>
                <TableBody>
                  {rows.map((r) => (
                    <TableRow key={r.row} className={r.match === "skip" ? "opacity-60" : ""} data-testid={`bulk-row-${r.row}`}>
                      <TableCell><Checkbox checked={!!included[r.row]} disabled={r.match === "skip"} onCheckedChange={(v) => setIncluded({ ...included, [r.row]: !!v })} /></TableCell>
                      <TableCell className="font-medium">{r.first_name} {r.last_name}</TableCell>
                      <TableCell className="font-mono text-xs">{r.license_number || "—"}{r.license_state ? ` · ${r.license_state}` : ""}</TableCell>
                      <TableCell className="font-mono text-xs">{r.dob || "—"}</TableCell>
                      <TableCell><Badge className={`${MATCH[r.match][0]} border`}>{MATCH[r.match][1]}</Badge>{r.problems?.length > 0 && <div className="text-[11px] text-rose-700">{r.problems.join("; ")}</div>}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label>Reports to order for each driver</Label>
                <div className="mt-2 grid grid-cols-1 gap-1.5">
                  {catalogTypes.map((c) => (
                    <label key={c.code} className="flex items-center justify-between rounded-md border border-slate-200 px-3 py-2 text-sm cursor-pointer hover:bg-slate-50">
                      <span className="flex items-center gap-2"><Checkbox checked={types.includes(c.code)} onCheckedChange={(v) => setTypes(v ? [...types, c.code] : types.filter((t) => t !== c.code))} data-testid={`bulk-type-${c.code}`} />{c.label}</span>
                      {c.price != null && <span className="font-mono text-xs text-slate-500">${Number(c.price).toFixed(2)}</span>}
                    </label>
                  ))}
                </div>
              </div>
              <div className="space-y-3">
                <div><Label>Batch label (optional)</Label><Input placeholder={`Annual MVR review ${new Date().getFullYear()}`} value={label} onChange={(e) => setLabel(e.target.value)} data-testid="bulk-label-input" /></div>
                <div className="rounded-md bg-slate-900 text-white p-4">
                  <div className="tag-mono text-slate-400 mb-1">{hasPrices ? "ESTIMATED CHARGE" : "BATCH SUMMARY"}</div>
                  <div className="text-2xl font-bold font-mono" data-testid="bulk-estimate">{hasPrices ? `$${estimate.toFixed(2)}` : `${chosen.length} × ${types.length || 0}`}</div>
                  {hasPrices && hasStateFee && <div className="text-[11px] text-slate-300 mt-1" data-testid="bulk-estimate-note">MVR is priced per driver based on the license state</div>}
                  <div className="text-xs text-slate-400">{chosen.length} driver(s) × {types.length} report type(s) · reference only, invoiced via QuickBooks</div>
                </div>
              </div>
            </div>
          </>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => { setOpen(false); reset(); }}>Cancel</Button>
          <Button onClick={submit} disabled={!parsed || busy || !chosen.length} className="bg-blue-700 hover:bg-blue-800" data-testid="bulk-submit-button">{busy && parsed ? "Submitting…" : `Create ${chosen.length} request${chosen.length === 1 ? "" : "s"}`}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
